import { ColumnType } from "antd/es/table"
import { IProjectDataSQP } from "../types/projectDataSQP.types"
import moment from "moment-timezone"

const useBaseColumns = (): ColumnType<IProjectDataSQP>[] => {
  function sortNullable(a?: string | number, b?: string | number) {
    const isEmpty = (v: unknown) => v === "" || v === null || v === undefined

    if (isEmpty(a) && isEmpty(b)) return 0
    if (isEmpty(a)) return -1
    if (isEmpty(b)) return 1
    return Number(a) - Number(b)
  }

  const columns: ColumnType<IProjectDataSQP>[] = [
    {
      title: `date`,
      key: "weekStartDate",
      dataIndex: "weekStartDate",
      render: (weekStartDate) => moment(weekStartDate).format("YYYY/MM/DD - WW"),
      width: 180,
    },
    {
      title: "Keyword SQ Volume",
      dataIndex: "searchQueryVolume",
      key: "searchQueryVolume",
      width: 160,
      sorter: (a, b) => a.searchQueryVolume - b.searchQueryVolume,
      defaultSortOrder: "descend",
    },
    {
      title: "Purchases Market",
      dataIndex: "purchasesMarket",
      key: "purchasesMarket",
      width: 150,
      sorter: (a, b) => a.purchasesMarket - b.purchasesMarket,
    },
    {
      title: "Purchases Top 3rd",
      dataIndex: "thirdTopCompetitorPurchases",
      key: "thirdTopCompetitorPurchases",
      width: 150,
      sorter: (a, b) => a.thirdTopCompetitorPurchases - b.thirdTopCompetitorPurchases,
    },
    {
      title: "Purchases my Asin",
      dataIndex: "myAsinsPurchases",
      key: "myAsinsPurchases",
      width: 180,
      sorter: (a, b) => a.myAsinsPurchases - b.myAsinsPurchases,
    },
    {
      title: "Purchases gap",
      dataIndex: "salesGap",
      key: "salesGap",
      width: 150,
      sorter: (a, b) => a.salesGap - b.salesGap,
      render: (value: number) => value,
    },
    {
      title: "Impressions my asin",
      dataIndex: "asinImpressionShare",
      key: "asinImpressionShare",
      width: 150,
      sorter: (a, b) => a.asinImpressionShare - b.asinImpressionShare,
      render: (value: number) => value + "%",
    },
    {
      title: "Clicks my Asin",
      key: "asinsClicksShare",
      width: 150,
      sorter: (a, b) => a.asinsClicksShare - b.asinsClicksShare,
      render: (value: IProjectDataSQP) =>
        value.asinsClicksShare ? value.asinsClicksShare + "%" : 0,
    },
    {
      title: "Clicks Top 3rd",
      dataIndex: "thirdTopCompetitorClickShare",
      key: "thirdTopCompetitorClickShare",
      width: 150,
      sorter: (a, b) => a.thirdTopCompetitorClickShare - b.thirdTopCompetitorClickShare,
      render: (value: number) => value + "%",
    },
    {
      title: "ATC my Asin",
      key: "atc",
      width: 150,
      render: (value: IProjectDataSQP) => (value.atc ? value.atc + "%" : 0 + "%"),
    },
    {
      title: "Purchases my Asin",
      key: "asinsPurchasesShare",
      width: 100,
      sorter: (a, b) => sortNullable(a.asinsPurchasesShare, b.asinsPurchasesShare),
      render: (value: IProjectDataSQP) => value.asinsPurchasesShare + "%",
    },
    {
      title: "Purchases Top 3rd",
      key: "thirdTopCompetitorPurchasesShare",
      width: 100,
      sorter: (a, b) =>
        sortNullable(a.thirdTopCompetitorPurchasesShare, b.thirdTopCompetitorPurchasesShare),
      render: (value: IProjectDataSQP) =>
        value.thirdTopCompetitorPurchasesShare ? value.thirdTopCompetitorPurchasesShare + "%" : 0,
    },
    {
      title: "CTR My Asin",
      key: "asinsCtr",
      width: 100,
      sorter: (a, b) => sortNullable(a.asinsCtr, b.asinsCtr),
      render: (value: IProjectDataSQP) => (value.asinsCtr ? value.asinsCtr + "%" : 0 + "%"),
    },
    {
      title: "CTR Market",
      key: "marketCtr",
      width: 100,
      sorter: (a, b) => sortNullable(a.marketCtr, b.marketCtr),
      render: (value: IProjectDataSQP) => (value.marketCtr ? value.marketCtr + "%" : 0),
    },
    {
      title: "ATCR My asin",
      key: "atcrMyAsin",
      width: 150,
      render: (value: IProjectDataSQP) => (value.atcrMyAsin ? value.atcrMyAsin + "%" : 0),
    },
    {
      title: "ATCR Market",
      key: "atcrMarket",
      width: 100,
      sorter: (a, b) => sortNullable(a.atcrMarket, b.atcrMarket),
      render: (value: IProjectDataSQP) => (value.atcrMarket ? value.atcrMarket + "%" : 0 + "%"),
    },
    {
      title: "CVR my asin",
      key: "asinCvr",
      width: 100,
      sorter: (a, b) => sortNullable(a.asinCvr, b.asinCvr),
      render: (value: IProjectDataSQP) => (value.asinCvr ? value.asinCvr + "%" : 0 + "%"),
    },
    {
      title: "CVR market",
      key: "marketCvr",
      width: 100,
      sorter: (a, b) => sortNullable(a.marketCvr, b.marketCvr),
      render: (value: IProjectDataSQP) => (value.marketCvr ? value.marketCvr + "%" : 0 + "%"),
    },
    {
      title: "CVR top 3 avg",
      key: "topThreeCompetitorCvrAvg",
      width: 180,
      sorter: (a, b) => sortNullable(a.topThreeCompetitorCvrAvg, b.topThreeCompetitorCvrAvg),
      render: (value: IProjectDataSQP) =>
        value.topThreeCompetitorCvrAvg ? value.topThreeCompetitorCvrAvg + "%" : 0 + "%",
    },
    {
      title: "Total sales",
      key: "totalSales",
      width: 150,
      render: (value: IProjectDataSQP) => "$" + value.totalSales,
    },
    {
      title: "Total SERP sales",
      key: "totalSerpSales",
      width: 180,
      sorter: (a, b) => sortNullable(a.totalSerpSales, b.totalSerpSales),
      render: (value: IProjectDataSQP) => "$" + value.totalSerpSales,
    },
    {
      title: "Organic sales",
      key: "organicSales",
      width: 150,
      sorter: (a, b) => sortNullable(a.organicSales, b.organicSales),
      render: (value: IProjectDataSQP) => "$" + value.organicSales,
    },
    {
      title: "SERP ad sales",
      key: "serpAdSales",
      width: 150,
      sorter: (a, b) => sortNullable(a.serpAdSales, b.serpAdSales),
      render: (value: IProjectDataSQP) => "$" + value.serpAdSales,
    },
    {
      title: "SERP Ad Cost",
      key: "serpAdCost",
      width: 180,
      sorter: (a, b) => sortNullable(a.serpAdCost, b.serpAdCost),
      render: (value: IProjectDataSQP) => (value.serpAdCost ? "$" + value.serpAdCost : "$" + 0),
    },
    {
      title: "SERP ACOS",
      key: "serpAcos",
      width: 100,
      sorter: (a, b) => sortNullable(a.serpAcos, b.serpAcos),
      render: (value: IProjectDataSQP) => (value.serpAcos ? value.serpAcos + "%" : 0 + "%"),
    },
    {
      title: "SERP TACOS",
      key: "serpTacos",
      width: 100,
      sorter: (a, b) => sortNullable(a.serpTacos, b.serpTacos),
      render: (value: IProjectDataSQP) => value.serpTacos + "%",
    },
    {
      title: "TACOS",
      key: "tacos",
      width: 100,
      sorter: (a, b) => sortNullable(a.tacos, b.tacos),
      render: (value: IProjectDataSQP) => (value.tacos ? value.tacos + "%" : 0 + "%"),
    },
    {
      title: "Other Ad Sales",
      key: "otherAdSales",
      width: 180,
      sorter: (a, b) => sortNullable(a.otherAdSales, b.otherAdSales),
      render: (value: IProjectDataSQP) => (value.otherAdSales ? "$" + value.otherAdSales : "$" + 0),
    },
    {
      title: "Other Ad Cost",
      key: "otherAdCost",
      dataIndex: "otherAdCost",
      width: 180,
      sorter: (a, b) => sortNullable(a.otherAdCost, b.otherAdCost),
      render: (otherAdCost: number) => (otherAdCost ? "$" + otherAdCost : "$" + 0),
    },
    {
      title: "Other Ad ACOS",
      key: "otherAdAcos",
      width: 180,
      sorter: (a, b) => sortNullable(a.otherAdAcos, b.otherAdAcos),
      render: (value: IProjectDataSQP) => (value.otherAdAcos ? value.otherAdAcos + "%" : 0 + "%"),
    },
  ]

  return columns
}

export default useBaseColumns
